import { apiClient } from '@/services/Axios';
import {
  mapLocalAssetFiltersToV1,
  toLegacyDetail,
  toLegacyEquipmentListContent,
  toLegacyPaginated,
  v1Delete,
  v1Get,
  v1Post,
  v1Put,
} from '@/services/v1Api';

export const getProjects = async (page = 1, perPage = 50) => {
    const { data, meta } = await v1Get('/api/v1/projects', { page, per_page: perPage });
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

export const getRecentProjects = async (limit = 5) => {
    const { data, meta } = await v1Get('/api/v1/projects', { sort: 'recent', pageSize: limit });
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

export const getProjectById = async (id) => {
    const { data } = await v1Get(`/api/v1/projects/${id}`);
    return toLegacyDetail(data);
};

export const createProject = async (projectData) => {
    const { data } = await v1Post('/api/v1/projects', projectData);
    return toLegacyDetail(data);
};

export const updateProject = async (id, projectData) => {
    const { data } = await v1Put(`/api/v1/projects/${id}`, projectData);
    return toLegacyDetail(data);
};

export const deleteProject = async (id) => {
    const { data } = await v1Delete(`/api/v1/projects/${id}`);
    return toLegacyDetail(data);
};

export const getProjectsByStatus = async (status) => {
    const { data, meta } = await v1Get('/api/v1/projects', { status });
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

export const getProjectsByDateRange = async (startDate, endDate) => {
    const { data, meta } = await v1Get('/api/v1/projects', {
        start_date: startDate,
        end_date: endDate,
    });
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

export const getProjectEquipmentLists = async (projectId, params = {}) => {
    const { data } = await v1Get(`/api/v1/projects/${projectId}/equipment-lists`, params);
    return toLegacyDetail(Array.isArray(data) ? data : data ?? []);
};

export const createEquipmentList = async (projectId, equipmentListData) => {
    const { data } = await v1Post(`/api/v1/projects/${projectId}/equipment-lists`, equipmentListData);
    return toLegacyDetail(data);
};

export const renumberEquipmentLists = async (projectId) => {
    const { data } = await v1Post(`/api/v1/projects/${projectId}/equipment-lists/renumber`);
    return toLegacyDetail(data);
};

export const copyEquipmentList = async (projectId, equipmentListId, copyData) => {
    const { data } = await v1Post(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/copy`,
        copyData,
    );
    return toLegacyDetail(data);
};

export const getEquipmentListContent = async (
    projectId,
    equipmentListId,
    hideAccessories = false,
    typeFilters = null,
    search = null,
) => {
    const params = {};
    if (hideAccessories) {
        params.hide_accessories = 'true';
    }
    if (typeFilters && typeof typeFilters === 'object') {
        if (typeFilters.show_assets !== undefined) params.show_assets = typeFilters.show_assets ? '1' : '0';
        if (typeFilters.show_crew !== undefined) params.show_crew = typeFilters.show_crew ? '1' : '0';
        if (typeFilters.show_transport !== undefined) params.show_transport = typeFilters.show_transport ? '1' : '0';
        if (typeFilters.show_temporary !== undefined) params.show_temporary = typeFilters.show_temporary ? '1' : '0';
    }
    if (search && typeof search === 'string' && search.trim()) {
        params.search = search.trim();
    }

    const { data, meta } = await v1Get(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}`,
        params,
    );
    return toLegacyEquipmentListContent(data, meta);
};

export const addEquipmentListContent = async (projectId, equipmentListId, contentData) => {
    const { data } = await v1Post(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/content`,
        contentData,
    );
    return toLegacyDetail(data);
};

export const updateEquipmentListContent = async (projectId, equipmentListId, contentId, updateData) => {
    const { data } = await v1Put(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/content/${contentId}`,
        updateData,
    );
    return toLegacyDetail(data);
};

export const reorderEquipmentListContent = async (projectId, equipmentListId, reorderData) => {
    const { data } = await v1Post(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/content/reorder`,
        reorderData,
    );
    return toLegacyDetail(data);
};

export const deleteEquipmentListContent = async (projectId, equipmentListId, contentId) => {
    const { data } = await v1Delete(
        `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/content/${contentId}`,
    );
    return toLegacyDetail(data);
};

export const getCompanyClassificationsTreeTable = async () => {
    const { data } = await v1Get('/api/v1/company/classifications/tree-table');
    return toLegacyDetail(data);
};

export const getLocalAssetFilterOptions = async () => {
    const { data } = await v1Get('/api/v1/local-assets/filter-options');
    return data;
};

export const getLocalAssets = async (filters = {}) => {
    const { data, meta } = await v1Get('/api/v1/local-assets', mapLocalAssetFiltersToV1(filters));
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

/**
 * Flat list endpoints use parent_id for hierarchy; chained dropdowns expect department_id / master_category_id.
 * @param {{ departments: unknown[], masterCategories: unknown[], categories: unknown[] }} raw
 */
function normalizeClassificationFilterOptions(raw) {
  const departments = Array.isArray(raw.departments) ? raw.departments : [];
  const masterCategories = (Array.isArray(raw.masterCategories) ? raw.masterCategories : []).map((mc) => ({
    ...mc,
    department_id: mc.department_id != null ? mc.department_id : mc.parent_id,
  }));
  const categories = (Array.isArray(raw.categories) ? raw.categories : []).map((cat) => ({
    ...cat,
    master_category_id: cat.master_category_id != null ? cat.master_category_id : cat.parent_id,
  }));
  return { departments, masterCategories, categories };
}

/**
 * Nested structure uses snake_case; some proxies may camelCase. Flatten for chained dropdowns.
 * @param {Record<string, unknown>} data — typically structure payload
 */
function flattenStructureFromApiData(data) {
  const departments = Array.isArray(data.departments) ? data.departments : [];
  const masterCategories = [];
  const categories = [];
  departments.forEach((dept) => {
    const mcs = dept.master_categories ?? dept.masterCategories ?? [];
    if (!Array.isArray(mcs)) {
      return;
    }
    mcs.forEach((mc) => {
      masterCategories.push({
        ...mc,
        department_id: dept.id,
      });
      const cats = mc.categories ?? mc.Categories ?? [];
      if (!Array.isArray(cats)) {
        return;
      }
      cats.forEach((cat) => {
        categories.push({
          ...cat,
          department_id: dept.id,
          master_category_id: mc.id,
        });
      });
    });
  });
  return { departments, masterCategories, categories };
}

/**
 * Laravel: `{ success, data: [...] }` or legacy `{ data: [...] }`. Never treat non-arrays as lists.
 * @param {unknown} data
 * @returns {unknown[]}
 */
function extractV1List(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
}

/** Flat endpoints always return parent_id; normalization adds department_id / master_category_id for filters. */
async function fetchFlatClassificationFilterOptions() {
  const fetchList = async (path, params = {}) => {
    try {
      const { data } = await v1Get(path, params);
      return extractV1List(data);
    } catch {
      return [];
    }
  };

  const [departments, masterCategories, categories] = await Promise.all([
    fetchList('/api/v1/company/classifications/departments'),
    fetchList('/api/v1/company/classifications/master-categories'),
    fetchList('/api/v1/company/classifications/categories', { show_all: true }),
  ]);

  return normalizeClassificationFilterOptions({
    departments,
    masterCategories,
    categories,
  });
}

export const getFilterOptions = async () => {
  const emptyLists = () => ({ departments: [], masterCategories: [], categories: [] });
  const [structureResult, flat] = await Promise.all([
    v1Get('/api/v1/company/classifications/structure', { show_all: true }).catch(() => null),
    fetchFlatClassificationFilterOptions().catch(() => emptyLists()),
  ]);

  let fromStructure = emptyLists();
  if (structureResult?.data) {
    const data = structureResult.data && typeof structureResult.data === 'object'
      ? structureResult.data
      : {};
    fromStructure = flattenStructureFromApiData(data);
  }

  const departments =
    fromStructure.departments.length > 0 ? fromStructure.departments : flat.departments;
  const masterCategories =
    flat.masterCategories.length > 0 ? flat.masterCategories : fromStructure.masterCategories;
  const categories = flat.categories.length > 0 ? flat.categories : fromStructure.categories;

  return {
    departments,
    masterCategories,
    categories,
  };
};

/**
 * Wiki catalog filters: company taxonomy merged with every classification id used on catalog rows
 * (including global IPS taxonomy), so dropdowns match {@code GET /api/v1/wiki/items} filters.
 */
export const getWikiCatalogFilterOptions = async () => {
  try {
    const { data } = await v1Get('/api/v1/wiki/classification-filter-options');
    const payload = data && typeof data === 'object' ? data : {};
    return normalizeClassificationFilterOptions({
      departments: payload.departments ?? [],
      masterCategories: payload.masterCategories ?? [],
      categories: payload.categories ?? [],
    });
  } catch (e) {
    console.warn('[getWikiCatalogFilterOptions] falling back to getFilterOptions', e);
    return getFilterOptions();
  }
};

export const getDepartments = async () => {
  try {
    const { data } = await v1Get('/api/v1/company/classifications/departments');
    const departments = extractV1List(data);
    return departments;
  } catch (error) {
    return [];
  }
};

export const getMasterCategories = async (departmentId) => {
  try {
    const { data } = await v1Get('/api/v1/company/classifications/structure', { show_all: true });
    const structure = data && typeof data === 'object' ? data : {};
    const departments = structure.departments || [];

    const department = departments.find(dept => dept.id == departmentId);
    if (department && department.master_categories) {
      return department.master_categories.map(mc => ({
        ...mc,
        department_id: department.id,
      }));
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const getCategories = async (masterCategoryId) => {
  try {
    const { data } = await v1Get('/api/v1/company/classifications/structure', { show_all: true });
    const structure = data && typeof data === 'object' ? data : {};
    const departments = structure.departments || [];

    let categories = [];
    departments.forEach(dept => {
      if (dept.master_categories) {
        dept.master_categories.forEach(mc => {
          if (mc.id == masterCategoryId && mc.categories) {
            categories = mc.categories.map(cat => ({
              ...cat,
              department_id: dept.id,
              master_category_id: mc.id,
            }));
          }
        });
      }
    });

    return categories;
  } catch (error) {
    return [];
  }
};

export const testLocalAssetsAPI = async (filters = {}) => {
    const { data, meta } = await v1Get('/api/v1/local-assets', mapLocalAssetFiltersToV1(filters));
    return toLegacyPaginated(Array.isArray(data) ? data : [], meta);
};

export const testAPIHealth = async () => {
    try {
        await apiClient.get('/api/v1/local-assets');
        await apiClient.get('/api/v1/local-assets?no_pagination=true');
        return true;
    } catch (error) {
        return false;
    }
};

export const getAvailableTabs = async () => {
    const { data } = await v1Get('/api/v1/options/project-tabs');
    return toLegacyDetail(data);
};

export const getProjectTabs = async (projectId) => {
    const { data } = await v1Get(`/api/v1/projects/${projectId}/tabs`);
    return toLegacyDetail(data);
};

export const addProjectTab = async (projectId, tabName) => {
    const { data } = await v1Post(`/api/v1/projects/${projectId}/tabs`, {
        tab_name: tabName,
    });
    return toLegacyDetail(data);
};

export const removeProjectTab = async (projectId, tabName) => {
    const { data } = await v1Delete(`/api/v1/projects/${projectId}/tabs/${tabName}`);
    return toLegacyDetail(data);
};
